import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from "react";
import html2canvas from "html2canvas";
import { Typography, Box } from "@mui/material";

const ImageOverlayUploader = forwardRef(({ selectedJob, open }, ref) => {
  const [image, setImage] = useState(null);
  const [originalImage, setOriginalImage] = useState(null);
  const containerRef = useRef();
  const fileInputRef = useRef();

  useEffect(() => {
    if (!open) {
      setImage(null);
      setOriginalImage(null);

      // clear file input value also
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  }, [open]);

  const lat =
    selectedJob?.locationCoordinates?.latitude ||
    selectedJob?.locationCoordinates?.lat;
  const lng =
    selectedJob?.locationCoordinates?.longitude ||
    selectedJob?.locationCoordinates?.lng;

  const address =
    `${selectedJob?.address}, ${selectedJob?.zipCode}` || "No Address";
  const locationName = `${selectedJob?.city || ""}, ${
    selectedJob?.state || ""
  }`;
  let key = process.env.REACT_APP_GOOGLE_MAP_API_KEY;

  const mapUrl =
    lat && lng
      ? `https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=18&size=200x200&maptype=hybrid&markers=color:red|${lat},${lng}&key=${key}`
      : "";

  // Upload image
  const handleUpload = (e) => {
    const file = e.target.files?.[0];

    if (file) {
      const originalUrl = URL.createObjectURL(file);

      setOriginalImage({
        url: originalUrl,
        file,
      });

      setImage(originalUrl);
    }
  };

  // Generate final image (IMPORTANT)
  const generateImage = async () => {
    if (!containerRef.current) return null;

    const canvas = await html2canvas(containerRef.current, {
      scale: 2,
      useCORS: true,
    });

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, "image/png");
    });
  };

  const triggerUpdateImage = () => {
    // open file selector
    fileInputRef.current?.click();
  };

  // expose to parent
  useImperativeHandle(ref, () => ({
    generateImage,
    triggerUpdateImage,
    getOriginalImage: () => originalImage?.file || null,
  }));
  const now = new Date();

  const formattedDate = now.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  const formattedTime = now.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div style={{ marginTop: "20px" }}>
      {/* Upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleUpload}
        style={{ display: "none" }}
      />

      {image && (
        <Box
          sx={{
            mt: 1,
            border: "1px dashed #9ca3af",
            borderRadius: "12px",
            p: 2,
            backgroundColor: "#fff",
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "#111827",
            }}
          >
            Upload New Installation Image
          </Typography>
          <div
            ref={containerRef}
            style={{
              position: "relative",
              marginTop: "10px",
            }}
          >
            {/* Main Image */}
            <img
              src={image}
              alt="uploaded"
              style={{ width: "100%", height: "auto" }}
              className=" object-cover aspect-[3/2] w-full"
            />

            {/* Black Strip Overlay */}
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                width: "100%",
                background: "rgba(0,0,0,0.8)",
                color: "#fff",
                display: "flex",
                gap: "10px",
                padding: "10px",
              }}
            >
              {/* Map */}
              {mapUrl && (
                <img
                  src={mapUrl}
                  alt="map"
                  style={{
                    width: "100px",
                    height: "100px",
                    objectFit: "cover",
                    borderRadius: "6px",
                  }}
                />
              )}

              {/* Text */}
              <div style={{ fontSize: "12px" }}>
                <div style={{ fontWeight: "bold" }}>{locationName}</div>
                <div style={{ textTransform: "uppercase" }}>{address}</div>
                <div>
                  Lat {lat || "-"}, Long {lng || "-"}
                </div>
                <div>
                  {formattedDate}, {formattedTime}
                </div>{" "}
              </div>
            </div>
          </div>
        </Box>
      )}
    </div>
  );
});

export default ImageOverlayUploader;
